import { useNavigate } from 'react-router-dom';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { useContractNegotiations } from '@/api/contract-negotiations';
import type { ContractListParams } from '@/api/contracts';
import type { ListQueryResult } from '../components/contract-list-section';
import { ContractListSection } from '../components/contract-list-section';
import type { ContractNegotiationRow } from '@/api/contract-negotiations';
import { negotiationColumns } from './negotiation-columns';
import { negotiationFilterFields } from './negotiation-filter-fields';

/**
 * Bridge: adapts the negotiation `useContractNegotiations` hook to the
 * `ContractListSection` `useListQuery` interface. The section passes
 * `filters` as a `Record<string, unknown>` where each key maps to a string[]
 * (from conditionsToFilters). Negotiation backend accepts flat query params
 * `status`, `priority`, `type`, `stage` — we extract the first value of each.
 */
function useNegotiationsQuery(params: ContractListParams): ListQueryResult<ContractNegotiationRow> {
  const filters = (params.filters ?? {}) as Record<string, string[]>;
  const status = filters['status']?.[0];
  const priority = filters['priority']?.[0];
  const type = filters['type']?.[0];
  const stage = filters['stage']?.[0];

  return useContractNegotiations({
    page: params.page ?? 1,
    page_size: params.page_size ?? 20,
    q: params.q,
    status,
    priority,
    type,
    stage,
  });
}

/**
 * Contract Negotiations list page — paginated table of all negotiations with
 * search, status/priority/type/stage filters, and column visibility. Clicking a
 * row navigates to the negotiation detail. Nav entry `contracts-negotiation`
 * already wires `/contracts/negotiations` in `nav.ts`.
 */
export default function ContractNegotiationsPage() {
  const navigate = useNavigate();

  return (
    <Container size="full">
      <PageHeader
        title="Contract Negotiations"
        description="All price negotiations across the deal pipeline."
        style={{ marginBottom: 'var(--vn-space-4)' }}
      />

      <ContractListSection<ContractNegotiationRow>
        useListQuery={useNegotiationsQuery}
        columns={negotiationColumns}
        getRowId={(r) => String(r.contract_negotiation_id)}
        onRowClick={(r) => navigate(`/contracts/negotiations/${r.contract_negotiation_id}`)}
        filterFields={negotiationFilterFields}
        searchPlaceholder="Search negotiations…"
        errorTitle="Failed to load negotiations"
        emptyTitle="No negotiations found"
        emptyDescription="Try adjusting your search or filters."
      />
    </Container>
  );
}
